import { useState, useEffect } from 'react'
import { Handle, Position, useNodes, useEdges } from 'reactflow'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { FileText, Loader2, Combine } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SynthesizerNodeDialog } from '@/components/ui/synthesizer-node-dialog'

interface SynthesizerNodeProps {
  data: {
    id: string
    content: string
    metadata: {
      synthesizedContent?: string
      status?: string
      error?: string
    }
  }
  selected: boolean
}

export function SynthesizerNode({
  data,
  selected,
}: SynthesizerNodeProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const nodes = useNodes()
  const edges = useEdges()

  console.log('SynthesizerNode rendered:', {
    id: data.id,
    content: data.content,
    metadata: data.metadata,
    selected
  })

  // Get all connected source nodes (data nodes connected as inputs)
  const getConnectedSourceNodes = () => {
    const sourceNodes = edges
      .filter(edge => edge.target === data.id)
      .map(edge => nodes.find(node => node.id === edge.source))
      .filter(node => node && node.type === 'data')

    console.log('Connected source nodes:', {
      nodeId: data.id,
      sourceNodes
    })

    return sourceNodes
  }

  // Synthesize content from connected nodes
  const synthesizeContent = () => {
    console.log('Starting content synthesis for node:', data.id)
    setIsLoading(true)
    setError(null)
    
    try {
      const sourceNodes = getConnectedSourceNodes()
      if (sourceNodes.length === 0) {
        throw new Error('No connected data nodes found')
      }

      // Collect content from all connected data nodes
      const contents = sourceNodes.map(node => {
        const typedData = node.data as {
          content?: string
          metadata?: {
            status?: string
            error?: string
            output?: string
          }
        }
        const content = typedData.content || ''
        const metadata = typedData.metadata || {}
        
        console.log('Processing source node content:', {
          nodeId: node.id,
          content,
          metadata,
          status: metadata.status
        })
        
        // Skip nodes that are still processing or have errors
        if (metadata.status === 'processing' || metadata.status === 'error') {
          return null
        }
        
        return metadata.output || content
      }).filter(Boolean)

      // Join all content with proper formatting
      const synthesizedContent = contents.join('\n\n')
      
      console.log('Synthesis complete:', {
        nodeId: data.id,
        synthesizedContent,
        sourceCount: contents.length
      })
      
      // Update node metadata
      data.metadata.synthesizedContent = synthesizedContent
      data.metadata.status = 'complete'
      
      // Show dialog with synthesized content
      setIsDialogOpen(true)
      
      setIsLoading(false)
    } catch (err) {
      console.error('Synthesis error:', err)
      const message = err instanceof Error ? err.message : String(err)
      setError(message)
      data.metadata.error = message
      data.metadata.status = 'error'
      setIsLoading(false)
    }
  }

  return (
    <div className="relative">
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-muted-foreground/50"
      />
      
      <Card
        className={cn(
          'p-4 w-[300px]',
          selected ? 'ring-2 ring-primary' : '',
          isLoading ? 'opacity-70' : ''
        )}
      >
        <div className="flex items-center gap-2 mb-2">
          <Combine className="w-5 h-5 text-primary" />
          <span className="font-medium">Synthesizer</span>
        </div>
        
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={synthesizeContent}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Synthesize Content
              </>
            )}
          </Button>
          {data.metadata.synthesizedContent && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsDialogOpen(true)}
            >
              <FileText className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        {error && (
          <p className="mt-2 text-sm text-red-500">{error}</p>
        )}
      </Card>

      <SynthesizerNodeDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        content={data.metadata.synthesizedContent || ''}
        status={data.metadata.status}
        error={data.metadata.error}
      />
    </div>
  )
}
